import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject, Signal } from '@angular/core';
import { AccountBalanceDto, BalancesControllerService } from '@api/client';
import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStoreFeature,
  type,
  withComputed,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap, tap } from 'rxjs';
import { BalanceViewModel } from '../dashboard.interfaces';

function equityInAsset(
  usdNotional: number | null | undefined,
  assetRefPrice: number | null | undefined,
): number {
  return usdNotional != null && assetRefPrice ? usdNotional / assetRefPrice : Infinity;
}

export const useBalances = () =>
  signalStoreFeature(
    {
      props: {
        _activeAccountId: type<Signal<number | undefined>>(),
        // Provided by useAssets — must be composed before this feature.
        assetScaleMap: type<Signal<Map<string, number | undefined>>>(),
      },
    },
    withState({
      balance: undefined as AccountBalanceDto | undefined,
      hideSmallBalances: false,
    }),
    withProps(() => ({
      _balancesService: inject(BalancesControllerService),
    })),
    withComputed(store => {
      const allBalances = computed(() => store.balance()?.assetBalances ?? []);

      const balances = computed(() => {
        const balances = allBalances();

        if (!store.hideSmallBalances()) {
          return balances;
        }

        return balances.filter(
          balance => balance.total !== 0 || balance.free !== 0 || balance.credit !== 0,
        );
      });

      const balancesViewModels = computed<BalanceViewModel[]>(() => {
        const accountBalance = store.balance();
        const assetScaleMap = store.assetScaleMap();

        return balances().map(balance => {
          const equity = equityInAsset(
            accountBalance?.usdNotional,
            balance.assetRefPrice,
          );

          return {
            ...balance,
            available: Math.max(0, Math.min(equity, balance.free, balance.total)),
            isEquityLimiting: equity < Math.min(balance.free, balance.total),
            scale: assetScaleMap.get(balance.asset),
          };
        });
      });

      return { allBalances, balances, balancesViewModels };
    }),
    withMethods(store => {
      const setBalance = (balance: AccountBalanceDto) => patchState(store, { balance });

      const setHideSmallBalances = (hideSmallBalances: boolean) =>
        patchState(store, { hideSmallBalances });

      const loadBalance = rxMethod<void>(
        pipe(
          switchMap(() =>
            store._balancesService.getBalancesV2(store._activeAccountId()).pipe(
              tapResponse({
                next: (balance: AccountBalanceDto) => setBalance(balance),
                error: (_error: HttpErrorResponse) => undefined,
              }),
            ),
          ),
        ),
      );

      const updateUSDRefCosts = rxMethod<{ code: string; usdRefCost: number }[]>(
        pipe(
          tap(useRefPrices => {
            const balance = store.balance();

            if (!balance) {
              return;
            }

            patchState(store, {
              balance: {
                ...balance,
                assetBalances: balance.assetBalances.map(assetBalance => ({
                  ...assetBalance,
                  assetRefPrice:
                    useRefPrices.find(refPrice => refPrice.code === assetBalance.asset)
                      ?.usdRefCost ?? assetBalance.assetRefPrice,
                })),
              },
            });
          }),
        ),
      );

      return { setBalance, setHideSmallBalances, loadBalance, updateUSDRefCosts };
    }),
  );
